/**
 * Page tree for the notes workspace. Reactive to PowerSync: pages created on
 * another device (or shared by an organization) appear as soon as the stream
 * synchronizes them.
 *
 * Integrated management:
 *   - Inline rename with double-click.
 *   - Drag & drop: dropping onto a page turns it into a subpage; dropping
 *     in the upper/lower quarter reorders among siblings (fractional sort_key
 *     → only one row written). movePageMutation prevents cycles.
 *   - Shared-page indicator (Users).
 */
import { createSignal, For, Show } from "solid-js";
import { Link, useNavigate, useSearch } from "@tanstack/solid-router";
import { ChevronDown, ChevronRight, Download, FileText, Plus, Trash2, Users } from "@fleet/icons";
import { buildPageDataset, cn, exportDataset, type ExportFormat } from "@folio/shared";
import { EXPORT_DELIVERY } from "@fleet/plugins/export";
import { useKernelService } from "@fleet/kernel";
import type { Page } from "@folio/powersync";
import { msg } from "@lingui/core/macro";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useI18n } from "@/providers/i18n-provider";
import { flattenPageTree, type PageTreeNode, usePageTree } from "../hooks/use-page-tree";
import { usePageSwipeGestures } from "../hooks/use-page-swipe-gestures";

const PAGE_DRAG_MIME = "application/x-folio-office-page";
const DROP_EDGE_RATIO = 0.25;

interface PageMoveTarget {
  parentPageId: string | null;
  afterSortKey?: string | null;
  beforeSortKey?: string | null;
}

interface NotesSidebarProps {
  activePageId: () => string | undefined;
  /** Local DB ready: gates the create/delete buttons (async mutation). */
  ready: () => boolean;
  onCreatePage: (opts?: { parentPageId?: string }) => void;
  onDeletePage: (page: Page) => void;
  onRenamePage: (id: string, title: string) => void;
  onMovePage: (pageId: string, target: PageMoveTarget) => void;
}

type DropPosition = "before" | "after" | "into" | null;

interface TreeItemContext {
  sidebar: NotesSidebarProps;
  editingId: () => string | null;
  setEditingId: (id: string | null) => void;
}

function dropPositionFromEvent(event: DragEvent, element: HTMLElement): DropPosition {
  const rect = element.getBoundingClientRect();
  const offsetY = event.clientY - rect.top;
  if (offsetY < rect.height * DROP_EDGE_RATIO) {
    return "before";
  }
  if (offsetY > rect.height * (1 - DROP_EDGE_RATIO)) {
    return "after";
  }
  return "into";
}

function PageTreeItem(props: {
  node: PageTreeNode;
  depth: number;
  index: () => number;
  siblings: () => PageTreeNode[];
  ctx: TreeItemContext;
}) {
  const { t } = useI18n();
  const [expanded, setExpanded] = createSignal(true);
  const [dropPos, setDropPos] = createSignal<DropPosition>(null);

  const sidebar = () => props.ctx.sidebar;
  const title = () => {
    const raw = props.node.page.title?.trim();
    return raw && raw.length > 0 ? raw : t(msg`Untitled`);
  };
  const isActive = () => sidebar().activePageId() === props.node.page.id;
  const isEditing = () => props.ctx.editingId() === props.node.page.id;
  const hasChildren = () => props.node.children.length > 0;

  const commitRename = (value: string) => {
    if (props.ctx.editingId() !== props.node.page.id) {
      return;
    }
    props.ctx.setEditingId(null);
    if (value !== (props.node.page.title ?? "")) {
      sidebar().onRenamePage(props.node.page.id, value);
    }
  };

  // ── Swipe-to-delete (touch) ──
  // Swipe the row left to delete; opens the same confirmation as the trash
  // button (deletes subpages in a cascade). The vertical axis yields to scrolling
  // and reorder drag-and-drop.
  let rowRef: HTMLDivElement | undefined = undefined;
  const swipe = usePageSwipeGestures({
    canSwipe: () => !isEditing(),
    getSurfaceRef: () => rowRef,
    onDelete: () => {
      sidebar().onDeletePage(props.node.page);
    },
  });

  const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const position = dropPos();
    setDropPos(null);
    const dragId = event.dataTransfer?.getData(PAGE_DRAG_MIME);
    if (!dragId || !position || dragId === props.node.page.id) {
      return;
    }

    if (position === "into") {
      const lastChild = props.node.children.at(-1);
      sidebar().onMovePage(dragId, {
        parentPageId: props.node.page.id,
        afterSortKey: lastChild?.page.sort_key ?? null,
        beforeSortKey: null,
      });
      setExpanded(true);
      return;
    }

    // Reorder among siblings: neighbors calculated without counting the dragged one
    const siblings = props.siblings().filter((sibling) => sibling.page.id !== dragId);
    const targetIndex = siblings.findIndex((sibling) => sibling.page.id === props.node.page.id);
    if (targetIndex === -1) {
      return;
    }
    const prev = position === "before" ? siblings[targetIndex - 1] : siblings[targetIndex];
    const next = position === "before" ? siblings[targetIndex] : siblings[targetIndex + 1];
    sidebar().onMovePage(dragId, {
      parentPageId: props.node.page.parent_page_id ?? null,
      afterSortKey: prev?.page.sort_key ?? null,
      beforeSortKey: next?.page.sort_key ?? null,
    });
  };

  return (
    <li class="motion-safe:animate-fade-in">
      <div class="relative">
        {/* Underlay revelado al deslizar: acción eliminar */}
        <Show when={swipe.tx() < 0}>
          <div
            aria-hidden="true"
            class={cn(
              "pointer-events-none absolute inset-0 flex items-center justify-end rounded-md pr-3 transition-colors",
              swipe.tx() < -swipe.COMMIT_THRESHOLD_PX
                ? "bg-danger/20 text-danger"
                : "bg-danger/10 text-danger/70",
            )}
          >
            <Trash2 class="h-4 w-4" />
          </div>
        </Show>
        <div
          ref={(element) => {
            rowRef = element;
          }}
          class={cn(
            "group relative flex items-center gap-1 rounded-md pr-1 transition-colors",
            isActive()
              ? "bg-accent/15 text-foreground"
              : "text-muted-foreground/80 hover:bg-accent/5 hover:text-foreground",
            dropPos() === "into" && "bg-accent/20 ring-1 ring-accent/40",
            dropPos() === "before" && "drop-indicator-before",
            dropPos() === "after" && "drop-indicator-after",
          )}
          style={{
            "padding-left": `${props.depth * 14}px`,
            "touch-action": "pan-y",
            transform: swipe.tx() === 0 ? undefined : `translate3d(${swipe.tx()}px, 0, 0)`,
            transition: swipe.isAnimating()
              ? "transform 220ms cubic-bezier(0.32, 0.72, 0, 1)"
              : undefined,
          }}
          onPointerDown={swipe.handlePointerDown}
          onPointerMove={swipe.handlePointerMove}
          onPointerUp={swipe.handlePointerUp}
          onPointerCancel={swipe.handlePointerCancel}
          draggable={!isEditing()}
          onDragStart={(event) => {
            event.dataTransfer?.setData(PAGE_DRAG_MIME, props.node.page.id);
            if (event.dataTransfer) {
              event.dataTransfer.effectAllowed = "move";
            }
          }}
          onDragOver={(event) => {
            if (event.dataTransfer?.types.includes(PAGE_DRAG_MIME)) {
              event.preventDefault();
              event.stopPropagation();
              setDropPos(dropPositionFromEvent(event, event.currentTarget));
            }
          }}
          onDragLeave={() => setDropPos(null)}
          onDrop={handleDrop}
        >
          <button
            type="button"
            class="flex h-6 w-5 shrink-0 items-center justify-center rounded text-muted-foreground/60 transition-transform motion-safe:will-change-transform hover:text-foreground active:scale-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            classList={{ invisible: !hasChildren() }}
            onClick={() => setExpanded((value) => !value)}
            aria-label={expanded() ? t(msg`Collapse`) : t(msg`Expand`)}
          >
            <Show when={expanded()} fallback={<ChevronRight class="h-3.5 w-3.5" />}>
              <ChevronDown class="h-3.5 w-3.5" />
            </Show>
          </button>
          <Show
            when={!isEditing()}
            fallback={
              <input
                type="text"
                class="min-w-0 flex-1 rounded bg-transparent py-1 text-sm outline-none ring-1 ring-accent/40"
                value={props.node.page.title ?? ""}
                ref={(element) => {
                  setTimeout(() => {
                    element.focus();
                    element.select();
                  }, 0);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    commitRename(event.currentTarget.value);
                  } else if (event.key === "Escape") {
                    props.ctx.setEditingId(null);
                  }
                }}
                onBlur={(event) => {
                  commitRename(event.currentTarget.value);
                }}
              />
            }
          >
            <Link
              to="/documents/$pageId"
              params={{ pageId: props.node.page.id }}
              class="flex min-w-0 flex-1 items-center gap-1.5 py-1 text-sm"
              onClick={(event: MouseEvent) => {
                // A swipe that committed a deletion must not navigate.
                if (swipe.getHasSwiped()) {
                  event.preventDefault();
                  swipe.resetHasSwiped();
                }
              }}
              onDblClick={(event: MouseEvent) => {
                event.preventDefault();
                props.ctx.setEditingId(props.node.page.id);
              }}
            >
              <span class="shrink-0 text-base leading-none">
                <Show
                  when={props.node.page.icon}
                  fallback={<FileText class="h-4 w-4 opacity-70" />}
                >
                  {props.node.page.icon}
                </Show>
              </span>
              <span class="truncate">{title()}</span>
              <Show when={props.node.page.is_shared === 1}>
                <Users class="h-3 w-3 shrink-0 opacity-60" aria-label={t(msg`Shared`)} />
              </Show>
            </Link>
          </Show>
          <button
            type="button"
            class="invisible h-6 w-6 shrink-0 rounded text-muted-foreground/60 transition-transform motion-safe:will-change-transform hover:text-foreground active:scale-90 focus-visible:visible focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent group-hover:visible disabled:cursor-not-allowed disabled:opacity-40"
            disabled={!sidebar().ready()}
            onClick={() => {
              sidebar().onCreatePage({ parentPageId: props.node.page.id });
            }}
            aria-label={t(msg`New subpage`)}
            title={t(msg`New subpage`)}
          >
            <Plus class="mx-auto h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            class="invisible h-6 w-6 shrink-0 rounded text-muted-foreground/60 transition-transform motion-safe:will-change-transform hover:text-danger active:scale-90 focus-visible:visible focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger group-hover:visible disabled:cursor-not-allowed disabled:opacity-40"
            disabled={!sidebar().ready()}
            onClick={() => {
              sidebar().onDeletePage(props.node.page);
            }}
            aria-label={t(msg`Move to trash`)}
            title={t(msg`Move to trash`)}
          >
            <Trash2 class="mx-auto h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <Show when={expanded() && hasChildren()}>
        <ul>
          <For each={props.node.children}>
            {(child, index) => (
              <PageTreeItem
                node={child}
                depth={props.depth + 1}
                index={index}
                siblings={() => props.node.children}
                ctx={props.ctx}
              />
            )}
          </For>
        </ul>
      </Show>
    </li>
  );
}

/**
 * Export menu: an "Export" button that opens a popover with CSV/PDF choices.
 * Mirrors the canon K1 `ExportMenu`; the data plumbing lives in the parent
 * (`onExport`), this is purely the trigger UI.
 */
function ExportMenu(props: { onExport: (format: ExportFormat) => void }) {
  const { t } = useI18n();
  return (
    <Popover>
      <PopoverTrigger as={Button} variant="ghost" size="sm" class="h-6 gap-1.5 px-2 text-xs">
        <Download class="h-3.5 w-3.5" />
        {t(msg`Export`)}
      </PopoverTrigger>
      <PopoverContent class="w-44 p-2">
        <div class="flex flex-col gap-1">
          <Button
            variant="ghost"
            size="sm"
            class="h-8 justify-start text-xs"
            onClick={() => props.onExport("csv")}
          >
            {t(msg`Export CSV`)}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            class="h-8 justify-start text-xs"
            onClick={() => props.onExport("pdf")}
          >
            {t(msg`Export PDF`)}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function NotesSidebar(props: NotesSidebarProps) {
  const { t } = useI18n();
  const { tree, pages } = usePageTree();
  const navigate = useNavigate();
  const delivery = useKernelService(EXPORT_DELIVERY);

  // ── Export ────────────────────────────────────────────────────────────────
  // Exports the notes currently shown in the tree (depth-first, same visual
  // order as the sidebar) to CSV or PDF via the shared, entity-agnostic export
  // utility. The page adapter lives in @folio/shared. 100 % local-first:
  // reads the already-synced pages from memory, no network.
  const handleExport = (format: ExportFormat) => {
    const target = delivery();
    if (!target) {
      return;
    }
    const currentPages = flattenPageTree(tree());
    const dataset = buildPageDataset(currentPages, {
      title: t(msg`Pages`),
      subtitle: t(msg`Exported notes`),
    });
    void exportDataset(dataset, format, target);
  };
  // #17b: inline rename is URL-addressable (`?rename=<id>`), so it survives
  // reload/deep-link instead of being ephemeral component state.
  const layoutSearch = useSearch({ from: "/_app" });
  const editingId = () => layoutSearch().rename ?? null;
  const setEditingId = (id: string | null) => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, rename: id ?? undefined }),
      replace: true,
    });
  };
  const [rootDropActive, setRootDropActive] = createSignal(false);

  const ctx: TreeItemContext = {
    sidebar: props,
    editingId,
    setEditingId,
  };

  // Drop in the empty nav area → move to the root, at the end
  const handleRootDrop = (event: DragEvent) => {
    event.preventDefault();
    setRootDropActive(false);
    const dragId = event.dataTransfer?.getData(PAGE_DRAG_MIME);
    if (!dragId) {
      return;
    }
    const roots = tree().filter((node) => node.page.id !== dragId);
    props.onMovePage(dragId, {
      parentPageId: null,
      afterSortKey: roots.at(-1)?.page.sort_key ?? null,
      beforeSortKey: null,
    });
  };

  return (
    <div class="flex h-full w-60 shrink-0 flex-col border-r border-border/40 bg-card/5">
      <div class="flex items-center justify-between px-3 py-2">
        <span class="text-xs font-semibold uppercase tracking-wide text-muted-foreground/70">
          {t(msg`Pages`)}
        </span>
        <div class="flex items-center gap-1">
          <ExportMenu onExport={handleExport} />
          <Button
            variant="ghost"
            size="icon"
            class="h-6 w-6"
            disabled={!props.ready()}
            onClick={() => {
              props.onCreatePage();
            }}
            aria-label={t(msg`New page`)}
            title={t(msg`New page`)}
          >
            <Plus class="h-4 w-4" />
          </Button>
        </div>
      </div>
      <nav
        aria-label={t(msg`Page tree`)}
        class={cn("flex-1 overflow-y-auto px-2 pb-4", rootDropActive() && "bg-accent/5")}
        onDragOver={(event) => {
          if (event.dataTransfer?.types.includes(PAGE_DRAG_MIME)) {
            event.preventDefault();
            setRootDropActive(true);
          }
        }}
        onDragLeave={() => setRootDropActive(false)}
        onDrop={handleRootDrop}
      >
        <Show
          when={(pages.data?.length ?? 0) > 0}
          fallback={
            <div class="flex flex-col items-center gap-3 px-3 py-8 text-center">
              <FileText class="h-7 w-7 text-muted-foreground/40" />
              <p class="text-xs leading-relaxed text-muted-foreground/70">
                {t(msg`No pages yet. Create your first note to get started.`)}
              </p>
              <Button
                size="sm"
                class="gap-1.5"
                disabled={!props.ready()}
                onClick={() => {
                  props.onCreatePage();
                }}
              >
                <Plus class="h-3.5 w-3.5" />
                {t(msg`Create page`)}
              </Button>
            </div>
          }
        >
          <ul class="space-y-0.5">
            <For each={tree()}>
              {(node, index) => (
                <PageTreeItem node={node} depth={0} index={index} siblings={tree} ctx={ctx} />
              )}
            </For>
          </ul>
        </Show>
      </nav>
    </div>
  );
}
